import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { MutationCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { MotionConfig } from "framer-motion";
import { LanguageProvider } from "./i18n";
import { AuthProvider } from "./context/AuthContext";
import App from "./App.tsx";
import { Toaster, toast } from "./components/ui/sonner";
import { lazy, Suspense } from "react";
import { useAuth } from "./context/AuthContext";
import { getCrmLanguage } from "./i18n/crm/locale";

// Kept out of the first download: the nudge pulls in CRM data hooks and only
// ever shows to a signed-in person, so public visitors never load it.
const NotifyNudge = lazy(() =>
  import("./components/chat/NotifyNudge").then((m) => ({ default: m.NotifyNudge })),
);
function SignedInNotifyNudge() {
  const { user } = useAuth();
  if (!user) return null;
  return (
    <Suspense fallback={null}>
      <NotifyNudge />
    </Suspense>
  );
}
import "./index.css";

const queryClient = new QueryClient({
  // A failed mutation used to be silent: the optimistic row reverted on refetch
  // and nothing told the operator why. Handling it on the cache makes feedback a
  // property of the client rather than something each call site remembers to add.
  // A mutation that renders its own inline error opts out with meta.silent.
  mutationCache: new MutationCache({
    onError: (_error, _vars, _ctx, mutation) => {
      if (mutation.meta?.silent) return;
      // The CRM dictionary is loaded on demand so it stays out of the public bundle.
      void import("./i18n/crm").then(({ crmDict }) =>
        toast.error(crmDict[getCrmLanguage()].forms.genericError),
      );
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

createRoot(document.getElementById("root")!).render(
  // reducedMotion="user" makes every framer-motion animation respect the OS
  // setting. The prefers-reduced-motion block in index.css only caps CSS
  // animation/transition durations, and framer-motion animates inline styles
  // from JS — so entrance animations and, worst, the spring-driven full-height
  // mobile drawer kept playing for users who had asked the OS to stop them.
  <MotionConfig reducedMotion="user">
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <LanguageProvider>
            <BrowserRouter>
              <App />
              <Toaster position="bottom-right" richColors closeButton />
              <SignedInNotifyNudge />
            </BrowserRouter>
          </LanguageProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </MotionConfig>
);
